import React, { useState, useEffect, useRef } from "react";
import { useSprings, animated } from "@react-spring/web";
import _ from "lodash";
import { useDevice } from "design_system/src";
import styles from "./HorizontalFoldingSlider.module.scss";


interface HorizontalFoldingSliderProps {
  name: string;
  data: React.ReactElement[];
  sectionRef?: React.RefObject<HTMLDivElement>;
}


const HorizontalFoldingSlider: React.FC<HorizontalFoldingSliderProps> = ({ name, data, sectionRef }) => {
  const { isMobile: isDeviceMobile, isLoading } = useDevice();
  const [isMobile, setIsMobile] = useState<boolean>();

  if (data.length <= 0) return null;

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isAutoPlay, setIsAutoPlay] = useState<boolean>(true);
  const internalSectionRef = useRef<HTMLDivElement>(null);
  const targetRef = sectionRef ?? internalSectionRef;

  useEffect(() => {
    if (!isLoading) {
      setIsMobile(isDeviceMobile);
    }

  }, [isDeviceMobile, isLoading]);

  useEffect(() => {
    if (!isAutoPlay) return undefined;
    const interval = setInterval(() => {
      setCurrentIndex((i) => i + 1 === data.length ? 0 : i + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlay, data.length]);

  const slide = useSprings(
    data.length,
    data.map((__, i) => ({
      width: i === currentIndex ? "90%" : `${10 / (data.length - 1)}%`,
      config: { tension: 280, friction: 30 },
    }))
  );

  const handleOnClick = (i: number) => {
    if (currentIndex !== i) {
      setCurrentIndex(i);
      setIsAutoPlay(false);
    }
  };

  if (isMobile === undefined) return <div/>;

  return (
    <>
      {isMobile && (
        <div>
          {slide.map((style, i) => (
            <div
              className={styles.SliderBackgroundMobile}
              style={{
                backgroundSize: `100% ${data.length * 100}%`,
                backgroundPosition: `center ${data.length * 100 * i * 1 / data.length}%`,
              }}
            >
              {data[i]}
            </div>
          ))}
        </div>
      )}
      {!isMobile && (
        <div ref={targetRef} className={styles.HorizontalFoldingSlider}>
          {slide.map((style, i) => (
            <animated.div
              key={`${name}-slide-${i}`}
              style={style}
            >
              <div
                className={styles.SliderBackground}
                style={{
                  backgroundSize: `${data.length * 100}% 100%`,
                  backgroundPosition: `${data.length * 100 * i * 1 / data.length}%`,
                }}
                onClick={() => handleOnClick(i)}
              >
                <div className={`${styles.SlideContent}${currentIndex === i ? ` ${styles.SlideContentVisible}` : ""}`}>
                  {data[i]}
                  <div className={styles.SliderNavigation}>
                    {_.range(0, data.length).map((index) => {
                      return <div
                        key={`${name}-navigation-${index}-slide-${i}`}
                        className={`${styles.SliderNavigationItem}${currentIndex === index ? ` ${styles["SliderNavigationItem--active"]}` : ""}`}
                        onClick={() => handleOnClick(index)}
                      />;
                    })}
                  </div>
                </div>
              </div>
            </animated.div>
          ))}
        </div>
      )}
    </>
  );
};

export default HorizontalFoldingSlider;
